import { useRef, useMemo, useState } from 'react';
import {
  Box,
  Flex,
  Avatar,
  Icon,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Button,
  Spinner,
  Center,
} from '@chakra-ui/react';
import { XIcon } from '@heroicons/react/solid';
import { CsvFailed } from '@/assets/icons/csv-export';
import NoTask from '@/assets/icons/ats/NoTask';
import { EmptyResults } from '@/components/ats/empty-results';
import { ScorecardBody } from '@/components/ats/collect-score-card/ScorecardBody';
import { AssessmentDetails } from '@/components/ats/job-stage-actions/actions/assessment/components/AssessmentDetails';
import { useGetAssessmentQuery } from '@/store/query/assessment';
import {
  useGetInterviewScorecardsQuery,
  useSubmitScoreCardMutation,
} from '@/store/query/ats-applications';
import { useScorecardAutoSaveHelper } from '@/components/ats/collect-score-card/useScorecardAutoSaveHelper';
import { useSelector } from 'react-redux';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import { useShowToast, useQueryParams } from '@/hooks';
import { AutoSaveLoadingIndicator } from '@/components/ats/collect-score-card/AutoSaveLoadingIndicator';
import { KText } from '@/ui-library/components/text';
import type { ReviewAssessmentMobileProps } from './ReviewAssessmentMobile.interface';

/**
 * Mobile full-screen review: Submission | Scorecard tabs. Submission is always
 * assessment details. SCORECARD UNAVAILABLE empty state appears only on Scorecard.
 */
export const ReviewAssessmentMobile = ({
  isOpen = true,
  onClose = () => {},
  application = {},
  assessmentId = null,
  showOnlyCurrUser = false,
  canFillForOthers = false,
  onSuccess = () => {},
}: ReviewAssessmentMobileProps) => {
  const formRef = useRef<{ submitForm: () => void } | null>(null);
  const currUser = useSelector(selectCurrUser);
  const queryParams = useQueryParams();
  const refParam = queryParams.get('ref');
  const [tabIndex, setTabIndex] = useState(0);

  const { data: assessmentData, isLoading: isAssessmentLoading } =
    useGetAssessmentQuery(
      { assessmentId: assessmentId ?? undefined },
      { skip: !assessmentId }
    );

  const applicationId = application?.id || assessmentData?.ats_application_id;

  const { data: scorecards, isLoading: isScLoading } =
    useGetInterviewScorecardsQuery(
      {
        applicationId: applicationId ?? '',
        params: { ats_assessment_id: assessmentId ?? undefined },
      },
      { skip: !assessmentId || !applicationId }
    );

  const [submitScoreCard, { isLoading: isSubmitting }] =
    useSubmitScoreCardMutation();
  const toast = useShowToast();

  const isLoading =
    isAssessmentLoading || isScLoading || (!!assessmentId && !applicationId);
  const isError =
    !isLoading &&
    (!assessmentData?.id || assessmentData?.status === 'cancelled');

  const firstScorecard = scorecards?.[0];
  const currUserScorecard = scorecards?.find(
    (sc) => sc?.reviewer?.id === currUser?.id
  );
  const isFillAssessmentMissingCurrUserScorecard =
    refParam === 'fill_assessment' && !isLoading && !currUserScorecard;
  const hideScorecardOnly =
    isFillAssessmentMissingCurrUserScorecard && !isError;
  const showScorecardUnavailable = hideScorecardOnly || !firstScorecard?.id;

  const assessmentUnavailableEmptyProps = {
    icon: CsvFailed,
    wrapperProps: {
      h: 'calc(100vh - 200px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    text: 'This assessment is no longer available',
    iconProps: { h: '140px', w: '130px' },
    subText: 'NOT AVAILABLE',
  };

  const scorecardUnavailableEmptyProps = {
    icon: NoTask,
    wrapperProps: {
      minH: '240px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    subText: 'SCORECARD UNAVAILABLE',
    subTextProps: { color: 'secondary-600' },
    text: 'This scorecard is no longer accepting responses',
    textProps: { color: 'secondary-900' },
    iconProps: { h: '140px', w: '130px', mb: '0px' },
  };

  const scoreCardReviewerIds = useMemo(
    () => scorecards?.map((s) => s?.reviewer?.id) || [],
    [scorecards]
  );
  const canSubmit =
    canFillForOthers || scoreCardReviewerIds.includes(currUser?.id);

  const { handleAutoSave, isSaved } = useScorecardAutoSaveHelper({
    atsApplicationId: applicationId,
    scorecardId: showOnlyCurrUser ? currUserScorecard?.id : firstScorecard?.id,
    questions: showOnlyCurrUser
      ? currUserScorecard?.questions
      : firstScorecard?.questions,
  });

  const closeDrawer = () => {
    onClose();
  };

  const handleSubmit = async (values: Record<string, unknown>) => {
    const questions = firstScorecard?.questions;
    const scorecardId = showOnlyCurrUser
      ? currUserScorecard?.id
      : firstScorecard?.id;

    const payload = {
      rating: values?.rating,
      feedback: values?.feedback,
      questions:
        questions?.map((item, idx) => {
          const param: { question?: unknown; answer?: unknown } = {
            question: item?.question,
          };
          if (values?.[idx + 1]) param.answer = values[idx + 1];
          return param;
        }) || [],
      status: 'submitted' as const,
    };

    const result = (await submitScoreCard({
      applicationId,
      scorecardId,
      payload,
    })) as { error?: unknown[] };

    if (result?.error?.length) {
      toast({
        title: 'Scorecard submission failed',
        status: 'error',
      });
      return;
    }
    toast({
      title: 'Scorecard submitted successfully',
      status: 'success',
    });

    onSuccess();
    closeDrawer();
  };

  const candidateName =
    firstScorecard?.candidate?.name ||
    assessmentData?.candidate?.name ||
    'Candidate';
  const candidateAvatar =
    firstScorecard?.candidate?.avatar || assessmentData?.candidate?.avatar;

  const headerHeight = '72px';

  if (!isOpen) return null;

  return (
    <Box
      pos="fixed"
      inset="0"
      bg="white"
      zIndex={1400}
      display="flex"
      flexDirection="column"
    >
      <Flex
        px="16px"
        py="12px"
        alignItems="center"
        gap="16px"
        borderBottom="1px solid"
        borderColor="border-color"
        minH={headerHeight}
        flexShrink={0}
      >
        <Icon
          aria-label="close"
          as={XIcon}
          w="20px"
          h="20px"
          cursor="pointer"
          onClick={closeDrawer}
        />
        <Flex align="center" gap="12px">
          <Avatar
            size="sm"
            name={candidateName}
            src={candidateAvatar}
            borderRadius="6px"
            width="32px"
            height="32px"
          />
          <Box>
            <KText size="md-medium" color="secondary-900">
              {candidateName}
            </KText>
            <KText size="md-regular" color="secondary-700">
              {application?.ats_job?.name ||
                assessmentData?.ats_job?.name ||
                ''}
            </KText>
          </Box>
        </Flex>
      </Flex>

      <Box px="16px" flex="1" minH={0} display="flex" flexDirection="column">
        <Tabs
          isFitted
          variant="unstyled"
          index={tabIndex}
          onChange={setTabIndex}
          display="flex"
          flexDirection="column"
          flex="1"
          minH={0}
        >
          <TabList
            sx={{ borderBottom: '1px solid', borderColor: 'border-color' }}
            mx="-16px"
            px="16px"
          >
            <Tab
              py="12px"
              fontWeight={550}
              border="none"
              borderRadius="0"
              _focus={{ boxShadow: 'none' }}
              _focusVisible={{ boxShadow: 'none' }}
              _active={{ bg: 'transparent' }}
              _selected={{
                color: 'primary-500',
                borderBottom: '2px solid',
                borderColor: 'primary-500',
              }}
            >
              Submission
            </Tab>
            <Tab
              py="12px"
              fontWeight={550}
              border="none"
              borderRadius="0"
              _focus={{ boxShadow: 'none' }}
              _focusVisible={{ boxShadow: 'none' }}
              _active={{ bg: 'transparent' }}
              _selected={{
                color: 'primary-500',
                borderBottom: '2px solid',
                borderColor: 'primary-500',
              }}
            >
              Scorecard
            </Tab>
          </TabList>

          <TabPanels flex="1" minH={0}>
            <TabPanel px={0} pt="22px" pb={24} h="100%" overflowY="auto">
              {isLoading ? (
                <Center h="calc(100vh - 200px)">
                  <Spinner />
                </Center>
              ) : isError ? (
                <EmptyResults {...assessmentUnavailableEmptyProps} />
              ) : (
                <AssessmentDetails
                  application={application}
                  assessmentId={assessmentId}
                  candidate={firstScorecard?.candidate}
                  assessmentData={assessmentData}
                  platform={assessmentData?.platform}
                />
              )}
            </TabPanel>

            <TabPanel px={0} pt="22px" pb={24} h="100%" overflowY="auto">
              {isLoading ? (
                <Center h="calc(100vh - 200px)">
                  <Spinner />
                </Center>
              ) : isError ? (
                <EmptyResults {...assessmentUnavailableEmptyProps} />
              ) : showScorecardUnavailable ? (
                <EmptyResults {...scorecardUnavailableEmptyProps} />
              ) : (
                <Box bg="base-white">
                  <ScorecardBody
                    scoreCardDetails={scorecards}
                    formRef={formRef}
                    handleSubmit={handleSubmit as unknown as () => void}
                    onSuccess={onSuccess}
                    showOnlyCurrUser={showOnlyCurrUser}
                    canFillForOthers={canFillForOthers}
                    isEdit={
                      !['closed', 'archived'].includes(
                        application?.ats_job?.job_status ?? ''
                      )
                    }
                    canSubmit={canSubmit}
                    skipHeader={true}
                    handleAutoSave={handleAutoSave}
                  />
                </Box>
              )}
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>

      {!isLoading &&
        !isError &&
        !hideScorecardOnly &&
        firstScorecard?.id &&
        tabIndex === 1 && (
          <Box
            borderTop="1px solid"
            borderColor="border-color"
            p="12px"
            flexShrink={0}
          >
            <Flex justify="space-between" mb="8px">
              <AutoSaveLoadingIndicator isSaved={isSaved} />
            </Flex>

            <Button
              w="100%"
              height="50px"
              minH="50px"
              variant="primary"
              isLoading={isSubmitting}
              onClick={() => formRef?.current?.submitForm()}
              disabled={!canSubmit}
            >
              Submit
            </Button>
          </Box>
        )}
    </Box>
  );
};
